import type { components } from '@multaqa/api-client'
import { isApiError } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

export type CompanyResource = components['schemas']['CompanyResource']
export type CompanySettingResource = components['schemas']['CompanySettingResource']
export type CompanyParentInput = components['schemas']['CompanyParentRequest']
export type CompanyUpdateInput = {
  legal_name?: Record<string, string>
  trading_name?: Record<string, string> | null
  code?: string
  default_locale?: 'en' | 'ar'
  registration_number?: string | null
  tax_identifier?: string | null
  fiscal_year_start_month?: number
}
export type CompanySettingsUpdateInput = {
  default_payment_terms_days?: number
  permission_monthly_minutes?: number
  branding_accent_color?: string | null
  branding_logo_path?: string | null
  timezone?: string
  default_returns_warehouse_id?: string | null
  default_fulfilment_warehouse_id?: string | null
  allow_negative_stock?: boolean
  default_expiry_convention?: 'on' | 'after'
  expiry_warning_days?: number
  default_allocation_strategy?: 'fefo' | 'fifo'
  over_receipt_policy?: components['schemas']['OverReceiptPolicy']
  require_approval_above_amount?: string | null
  approval_threshold_currency?: string | null
}
export type CompanyOption = {
  id: string
  name: Record<string, string>
  parent_id?: string | null
}

const companyOptionSchema = z.object({
  id: z.string(),
  name: z.record(z.string(), z.string()),
  parent_id: z.string().nullable().optional(),
})

function companyKey(groupId: string, companyId: string) {
  return ['platform', 'groups', groupId, 'companies', companyId] as const
}

function bodyOrThrow<T>(body: { data: T } | undefined, endpoint: string): T {
  if (!body) throw new Error(`${endpoint} returned no body`)
  return body.data
}

export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404
}

export function useCompanyDetail(groupId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: companyKey(groupId ?? '', companyId ?? ''),
    enabled: Boolean(groupId && companyId),
    queryFn: async (): Promise<CompanyResource> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}', {
        params: { path: { group: groupId ?? '', company: companyId ?? '' } },
      })
      return bodyOrThrow(result.data, 'GET /platform/groups/{group}/companies/{company}')
    },
  })
}

export function useCompanyOptions(groupId: string | null) {
  return useQuery({
    queryKey: ['platform', 'groups', groupId, 'companies'] as const,
    enabled: Boolean(groupId),
    queryFn: async (): Promise<CompanyOption[]> => {
      const result = await api.GET('/platform/groups/{group}/companies', {
        params: { path: { group: groupId ?? '' } },
      })
      return z
        .array(companyOptionSchema)
        .parse(bodyOrThrow(result.data, 'GET /platform/groups/{group}/companies'))
    },
  })
}

export function useUpdateCompany() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      input,
    }: {
      groupId: string
      companyId: string
      input: CompanyUpdateInput
    }): Promise<CompanyResource> => {
      const result = await api.PATCH('/platform/groups/{group}/companies/{company}', {
        params: { path: { group: groupId, company: companyId } },
        body: input as unknown as components['schemas']['CompanyUpdateRequest'],
      })
      return bodyOrThrow(result.data, 'PATCH /platform/groups/{group}/companies/{company}')
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: companyKey(variables.groupId, variables.companyId),
      })
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'companies'],
      })
    },
  })
}

export function useSetCompanyStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      status,
    }: {
      groupId: string
      companyId: string
      status: components['schemas']['CompanyStatus']
    }): Promise<CompanyResource> => {
      const result = await api.PATCH('/platform/groups/{group}/companies/{company}/status', {
        params: { path: { group: groupId, company: companyId } },
        body: { status },
      })
      return bodyOrThrow(result.data, 'PATCH /platform/groups/{group}/companies/{company}/status')
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: companyKey(variables.groupId, variables.companyId),
      })
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'companies'],
      })
    },
  })
}

export function useSetCompanyParent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      input,
    }: {
      groupId: string
      companyId: string
      input: CompanyParentInput
    }): Promise<CompanyResource> => {
      const result = await api.PATCH('/platform/groups/{group}/companies/{company}/parent', {
        params: { path: { group: groupId, company: companyId } },
        body: input,
      })
      return bodyOrThrow(result.data, 'PATCH /platform/groups/{group}/companies/{company}/parent')
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: companyKey(variables.groupId, variables.companyId),
      })
      void queryClient.invalidateQueries({
        queryKey: ['platform', 'groups', variables.groupId, 'companies'],
      })
    },
  })
}

export function useCompanySettings(groupId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: [...companyKey(groupId ?? '', companyId ?? ''), 'settings'] as const,
    enabled: Boolean(groupId && companyId),
    queryFn: async (): Promise<CompanySettingResource> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}/settings', {
        params: { path: { group: groupId ?? '', company: companyId ?? '' } },
      })
      return bodyOrThrow(result.data, 'GET /platform/groups/{group}/companies/{company}/settings')
    },
  })
}

/** The company's logo bytes for the branding preview; the stored path keys it, so a new upload refetches. */
export function useCompanyLogoPreview(groupId: string, companyId: string, logoPath: string | null) {
  return useQuery({
    queryKey: [...companyKey(groupId, companyId), 'logo', logoPath] as const,
    enabled: Boolean(logoPath),
    retry: false,
    queryFn: async (): Promise<Blob> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}/settings/logo', {
        params: { path: { group: groupId, company: companyId } },
        parseAs: 'blob',
      })
      if (!result.data) throw new Error('GET company logo returned no body')
      return result.data
    },
  })
}

export function useUpdateCompanySettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      input,
    }: {
      groupId: string
      companyId: string
      input: CompanySettingsUpdateInput
    }): Promise<CompanySettingResource> => {
      const result = await api.PATCH('/platform/groups/{group}/companies/{company}/settings', {
        params: { path: { group: groupId, company: companyId } },
        body: input as unknown as components['schemas']['CompanySettingUpdateRequest'],
      })
      return bodyOrThrow(result.data, 'PATCH /platform/groups/{group}/companies/{company}/settings')
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [...companyKey(variables.groupId, variables.companyId), 'settings'],
      })
    },
  })
}
